import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import isEmpty from "lodash-es/isEmpty";

import Alert from "@mui/material/Alert";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import { MobileDateRangePicker } from "@mui/x-date-pickers-pro/MobileDateRangePicker";

import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";

import { useJwtStorage, useLocalStorage } from "../../hooks/useLocalStorage";
import AccordionTitle from "../../components/AccordionTitle";

import DrawerAppBar from "../../components/DrawerAppBar";
import ProgressBar from "../../components/ProgressBar";
import Title from "../../components/Title";

import { doService } from "../../services/ServiceHelper";
import {
  StripedTableCell,
  StripedTableHeadCell,
  StripedTableRow,
} from "../../components/StripedTable";
import {
  getUserSummary,
  getStudentSummaryRange,
} from "../../services/UserService";
import TableRowNoResults from "../../components/TableRowNoResults";
import { getPassStatusText } from "../../services/PassService";
import {
  getPeriodName,
  isActivitiesEnabled,
  isCourseEnabled,
  isEventEnabled,
  isPassEnabled,
  isScoreEnabled,
  isMeetingEnabled,
  isCommunityServiceEnabled,
  isCheckinEnabled,
} from "../../services/SchoolService";
import {
  getCourseStartTime,
  getCourseEndTime,
} from "../../services/CourseService";
import {
  getEventEndTime,
  getEventStartTime,
} from "../../services/EventService";

import { TableHead } from "@mui/material";
import MyTabs from "../../components/MyTabs";
import { getCommServiceStatusText } from "../../services/CommServiceService";
import { getMS, getHM2 } from "../../services/StringService";
import MetricCard from "../../components/MetricCard";
import { getAverage, getMedian } from "../../services/NumberService";

/**
 *
 */
const StudentProfile = () => {
  const { eid } = useParams();
  const [_school] = useLocalStorage("Auth", "school", null);
  const [_jwt] = useJwtStorage();

  const [_showProgress, setShowProgress] = useState(false);
  const [_student, setStudent] = useState({});
  const [_dateRange, setDateRange] = useState([
    dayjs().subtract(30, "days"),
    dayjs(),
  ]);

  const [_passes, setPasses] = useState([]);
  const [_activities, setActivities] = useState([]);
  const [_events, setEvents] = useState([]);
  const [_courses, setCourses] = useState([]);
  const [_scores, setScores] = useState([]);
  const [_meetings, setMeetings] = useState([]);
  const [_serviceOps, setServiceOps] = useState([]);
  const [_searchPerformed, setSearchPerformed] = useState(false);
  const [_enabledTabs, setEnabledTabs] = useState([]);
  const [_totalSeconds, setTotalSeconds] = useState(0);
  const [_averageSeconds, setAverageSeconds] = useState(0);
  const [_medianSeconds, setMedianSeconds] = useState(0);

  const [_selectedTabId, setSelectedTabId] = useState("");

  useEffect(() => {
    initContent();
    initTabs();
  }, []);

  const initTabs = () => {
    const tabs = [];

    if (isActivitiesEnabled(_school)) {
      tabs.push({
        id: "activityTab",
        label: "Activities",
      });
    }

    if (isPassEnabled(_school)) {
      tabs.push({
        id: "passTab",
        label: "Passes",
      });
    }

    if (isScoreEnabled(_school)) {
      if (isCheckinEnabled(_school)) {
        tabs.push({
          id: "scoreTab",
          label: "Checkins",
        });
      } else {
        tabs.push({
          id: "scoreTab",
          label: "Scores",
        });
      }
    }

    if (isEventEnabled(_school)) {
      tabs.push({
        id: "eventTab",
        label: "Events",
      });
    }

    if (isMeetingEnabled(_school)) {
      tabs.push({
        id: "meetingTab",
        label: "Meetings",
      });
    }

    if (isCourseEnabled(_school)) {
      tabs.push({
        id: "courseTab",
        label: "Courses",
      });
    }

    if (isCommunityServiceEnabled(_school)) {
      tabs.push({
        id: "opsTab",
        label: "Service Ops",
      });
    }

    setEnabledTabs(tabs);
  };

  const initContent = () => {
    setShowProgress(true);

    doService(getUserSummary(_school, _jwt, eid), (result) => {
      setStudent(result.data);
      setShowProgress(false);
    });
  };

  const handleChangeDateRange = (value) => {
    setDateRange(value);
  };

  const handleSearch = () => {
    const startDate = _dateRange[0].format("YYYY-MM-DD");
    const endDate = _dateRange[1].format("YYYY-MM-DD");

    setShowProgress(true);
    doService(
      getStudentSummaryRange(_school, _jwt, eid, startDate, endDate),
      (results) => {
        setShowProgress(false);
        setActivities(results.data.activities);
        setPasses(results.data.passes);
        initDurationMetrics(results.data.passes);
        setEvents(results.data.events);
        setMeetings(results.data.meetings);
        setScores(results.data.scores);
        setSearchPerformed(true);
        setCourses(results.data.courses);
        setServiceOps(results.data.ops);
      }
    );
  };

  const initDurationMetrics = (rows) => {
    let totalSeconds = 0;
    const arry = [];

    for (const row of rows) {
      totalSeconds += row.secondsElapsed;
      arry.push(row.secondsElapsed);
    }

    setMedianSeconds(getMedian(arry));
    setAverageSeconds(getAverage(arry));
    setTotalSeconds(totalSeconds);
  };

  const onTabIdChange = (id) => {
    setSelectedTabId(id);
  };

  return (
    <>
      <DrawerAppBar />

      <Container sx={{ mt: 1 }}>
        <Title text="Student Profile" showBack={true} />
        <ProgressBar showProgress={_showProgress} />

        <Alert severity="info">
          Use the Search Criteria to see a specific snapshot of the student
          actions.
        </Alert>

        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={6}>
            <Accordion defaultExpanded sx={{ mt: 1 }}>
              <AccordionSummary className="accordionHeader">
                <AccordionTitle text="Student Summary" />
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer component={Paper} sx={{ mt: 1, mb: 1 }}>
                  <Table size="small">
                    <TableBody>
                      <StripedTableRow>
                        <StripedTableHeadCell>
                          Student Name
                        </StripedTableHeadCell>
                        <StripedTableCell>
                          {_student.first_name} {_student.last_name}
                          {isEmpty(_student.mi) === false && (
                            <> {_student.mi}</>
                          )}
                        </StripedTableCell>
                      </StripedTableRow>
                      <StripedTableRow>
                        <StripedTableHeadCell>
                          Student Grade
                        </StripedTableHeadCell>
                        <StripedTableCell>{_student.grade}</StripedTableCell>
                      </StripedTableRow>
                      <StripedTableRow>
                        <StripedTableHeadCell>
                          Student Email
                        </StripedTableHeadCell>
                        <StripedTableCell>{_student.email}</StripedTableCell>
                      </StripedTableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          </Grid>
          <Grid item xs={6}>
            <Accordion defaultExpanded sx={{ mt: 1 }}>
              <AccordionSummary className="accordionHeader">
                <AccordionTitle text="Search Criteria" />
              </AccordionSummary>
              <AccordionDetails>
                <MobileDateRangePicker
                  value={_dateRange}
                  sx={{ mt: 1, mb: 1 }}
                  onChange={handleChangeDateRange}
                />

                <Button
                  sx={{ mt: 0.5 }}
                  variant="contained"
                  fullWidth
                  onClick={handleSearch}
                  className="button1"
                >
                  Search
                </Button>
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>

        {_searchPerformed === true && (
          <>
            <Accordion defaultExpanded sx={{ mt: 1 }}>
              <AccordionSummary className="accordionHeader">
                <AccordionTitle
                  text={`Search Results: from ${_dateRange[0].format(
                    "MM/DD/YY"
                  )} to ${_dateRange[1].format("MM/DD/YY")}`}
                />
              </AccordionSummary>
              <AccordionDetails>
                <MyTabs tabs={_enabledTabs} onTabIdChange={onTabIdChange} />

                {_selectedTabId === "activityTab" && (
                  <TableContainer component={Paper} sx={{ mt: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <StripedTableRow>
                          <StripedTableCell align="center">
                            Date
                          </StripedTableCell>
                          <StripedTableCell>Period</StripedTableCell>
                          <StripedTableCell>Activity</StripedTableCell>
                          <StripedTableCell>Proctor</StripedTableCell>
                          <StripedTableCell>Type</StripedTableCell>
                        </StripedTableRow>
                      </TableHead>
                      <TableBody>
                        <TableRowNoResults data={_activities} />
                        {_activities.map((activity) => (
                          <StripedTableRow>
                            <StripedTableCell align="center">
                              {dayjs(activity.date).format("MM/DD")}
                            </StripedTableCell>
                            <StripedTableCell>
                              {getPeriodName(_school, activity.period - 1)}
                            </StripedTableCell>
                            <StripedTableCell>{activity.name}</StripedTableCell>
                            <StripedTableCell>
                              {activity.teacherLastName},{" "}
                              {activity.teacherFirstName.substring(0, 1)}
                            </StripedTableCell>
                            <StripedTableCell>
                              {activity.courseName != null && (
                                <>Focused for {activity.courseName}</>
                              )}
                              {activity.courseName == null && (
                                <>Flex</>
                              )}
                            </StripedTableCell>
                          </StripedTableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {_selectedTabId === "passTab" && (
                  <>
                    <Grid container spacing={2} sx={{ mt: 2 }}>
                      <Grid item xs={3} align={"center"}>
                        <MetricCard
                          title={"Total Pass Time:"}
                          color={"#ba000d"}
                          textColor={"#ffffff"}
                          content={<>{getHM2(_totalSeconds)}</>}
                        />
                      </Grid>
                      <Grid item xs={3} align={"center"}>
                        <MetricCard
                          title={"Average Pass Time:"}
                          color={"#2c387e"}
                          textColor={"#ffffff"}
                          content={<>{getHM2(_averageSeconds)}</>}
                        />
                      </Grid>{" "}
                      <Grid item xs={3} align={"center"}>
                        <MetricCard
                          title={"Median Pass Time:"}
                          color={"#2196f3"}
                          textColor={"#ffffff"}
                          content={<>{getHM2(_medianSeconds)}</>}
                        />
                      </Grid>
                    </Grid>
                    <TableContainer component={Paper} sx={{ mt: 1 }}>
                      <Table size="small">
                        <TableHead>
                          <StripedTableRow>
                            <StripedTableCell align="center">
                              Created
                            </StripedTableCell>
                            <StripedTableCell align="center">
                              Approved
                            </StripedTableCell>
                            <StripedTableCell>Type</StripedTableCell>
                            <StripedTableCell>Status</StripedTableCell>
                            <StripedTableCell>From</StripedTableCell>
                            <StripedTableCell align="center">
                              Elapsed
                            </StripedTableCell>
                          </StripedTableRow>
                        </TableHead>

                        <TableBody>
                          <TableRowNoResults data={_passes} />
                          {_passes.map((p) => (
                            <StripedTableRow>
                              <StripedTableCell align="center">
                                {dayjs(p.date).format("MM/DD")}
                              </StripedTableCell>
                              <StripedTableCell align="center">
                                {p.approvedAt == null && "N/A"}
                                {p.approvedAt != null &&
                                  dayjs(p.approvedAt).format("MM/DD hh:mm a")}
                              </StripedTableCell>
                              <StripedTableCell>
                                {p.passTypeName}
                              </StripedTableCell>
                              <StripedTableCell>
                                {getPassStatusText(p.status)}
                              </StripedTableCell>
                              <StripedTableCell>
                                {p.teacherLastName},{" "}
                                {p.teacherFirstName.substring(0, 1)}
                              </StripedTableCell>
                              <StripedTableCell align="center">
                                {getMS(p.secondsElapsed)}
                              </StripedTableCell>
                            </StripedTableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}

                {_selectedTabId === "scoreTab" && (
                  <>
                    <Accordion defaultExpanded sx={{ mt: 1 }}>
                      <AccordionSummary className="accordionHeader">
                        <AccordionTitle text="Scores" />
                      </AccordionSummary>
                      <AccordionDetails>
                        <TableContainer component={Paper} sx={{ mt: 1 }}>
                          <Table size="small">
                            <TableHead>
                              <StripedTableRow>
                                <StripedTableCell align="center">
                                  Date
                                </StripedTableCell>
                                {isCheckinEnabled(_school) && (
                                  <StripedTableCell align="center">
                                    Time
                                  </StripedTableCell>
                                )}
                                <StripedTableCell>Category</StripedTableCell>
                                {!isCheckinEnabled(_school) && (
                                  <>
                                    <StripedTableCell align="center">
                                      Period
                                    </StripedTableCell>
                                    <StripedTableCell>
                                      Consequence
                                    </StripedTableCell>
                                    <StripedTableCell>Teacher</StripedTableCell>
                                    <StripedTableCell>Comment</StripedTableCell>
                                  </>
                                )}
                              </StripedTableRow>
                            </TableHead>
                            <TableBody>
                              <TableRowNoResults data={_scores} />
                              {_scores.map((score) => (
                                <StripedTableRow>
                                  <StripedTableCell align="center">
                                    {dayjs(score.scoreDate).format("MM/DD")}
                                  </StripedTableCell>

                                  {isCheckinEnabled(_school) && (
                                    <StripedTableCell align="center">
                                      {dayjs(score.dateCreated).format(
                                        "hh:mm a"
                                      )}
                                    </StripedTableCell>
                                  )}

                                  <StripedTableCell>
                                    {score.categoryName}
                                  </StripedTableCell>
                                  {!isCheckinEnabled(_school) && (
                                    <>
                                      <StripedTableCell align="center">
                                        {score.period}
                                      </StripedTableCell>
                                      <StripedTableCell>
                                        {score.consequenceName}
                                      </StripedTableCell>
                                      <StripedTableCell>
                                        {score.teacherLastName},{" "}
                                        {score.teacherFirstName.substring(0, 1)}
                                      </StripedTableCell>
                                      <StripedTableCell>
                                        {!isEmpty(score.comment) &&
                                          score.comment}
                                        {isEmpty(score.comment) && "--NONE--"}
                                      </StripedTableCell>
                                    </>
                                  )}
                                </StripedTableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                    <Accordion defaultExpanded sx={{ mt: 1 }}>
                      <AccordionSummary className="accordionHeader">
                        <AccordionTitle text="Fulfillments" />
                      </AccordionSummary>
                      <AccordionDetails>
                        <TableContainer component={Paper} sx={{ mt: 1 }}>
                          <Table size="small">
                            <TableHead>
                              <StripedTableRow></StripedTableRow>
                            </TableHead>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                    <Accordion defaultExpanded sx={{ mt: 1 }}>
                      <AccordionSummary className="accordionHeader">
                        <AccordionTitle text="Consequences without Fulfillments" />
                      </AccordionSummary>
                      <AccordionDetails>
                        <TableContainer component={Paper} sx={{ mt: 1 }}>
                          <Table size="small">
                            <TableHead>
                              <StripedTableRow></StripedTableRow>
                            </TableHead>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                  </>
                )}

                {_selectedTabId === "courseTab" && (
                  <TableContainer component={Paper} sx={{ mt: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <StripedTableRow>
                          <StripedTableCell align="center">
                            Date
                          </StripedTableCell>
                          <StripedTableCell align="center">
                            Period
                          </StripedTableCell>

                          <StripedTableCell>Name</StripedTableCell>
                          <StripedTableCell>Proctor</StripedTableCell>
                          <StripedTableCell>Location</StripedTableCell>
                        </StripedTableRow>
                      </TableHead>
                      <TableBody>
                        <TableRowNoResults data={_courses} />
                        {_courses.map((c) => (
                          <>
                            <StripedTableRow>
                              <StripedTableCell align="center">
                                {dayjs(c.date).format("MM/DD")}
                              </StripedTableCell>
                              <StripedTableCell align="center">
                                {getCourseStartTime(_school, c)}-
                                {getCourseEndTime(_school, c)}
                              </StripedTableCell>
                              <StripedTableCell>{c.name}</StripedTableCell>
                              <StripedTableCell>
                                {c.last_name}, {c.first_name.substring(0, 1)}
                              </StripedTableCell>
                              <StripedTableCell>
                                {c.schoolLocationName}
                              </StripedTableCell>
                            </StripedTableRow>
                          </>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {_selectedTabId === "eventTab" && (
                  <TableContainer component={Paper} sx={{ mt: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <StripedTableRow>
                          <StripedTableCell align="center">
                            Date
                          </StripedTableCell>
                          <StripedTableCell align="center">
                            Time
                          </StripedTableCell>
                          <StripedTableCell>Event Type</StripedTableCell>
                          <StripedTableCell>Title</StripedTableCell>
                          <StripedTableCell>Owner</StripedTableCell>
                        </StripedTableRow>
                      </TableHead>

                      <TableBody>
                        <TableRowNoResults data={_events} />
                        {_events.map((e) => (
                          <>
                            <StripedTableRow>
                              <StripedTableCell align="center">
                                {dayjs(e.startDate).format("MM/DD")}
                              </StripedTableCell>
                              <StripedTableCell align="center">
                                {getEventStartTime(e)}-{getEventEndTime(e)}
                              </StripedTableCell>
                              <StripedTableCell>
                                {e.eventTypeName}
                              </StripedTableCell>
                              <StripedTableCell>{e.title}</StripedTableCell>
                              <StripedTableCell>
                                {e.ownerLastName},{" "}
                                {e.ownerFirstName.substring(0, 1)}
                              </StripedTableCell>
                            </StripedTableRow>
                          </>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {_selectedTabId === "meetingTab" && (
                  <TableContainer component={Paper} sx={{ mt: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <StripedTableRow>
                          <StripedTableCell align="center">
                            Date
                          </StripedTableCell>
                          <StripedTableCell align="center">
                            Time
                          </StripedTableCell>
                          <StripedTableCell>Meeting Type</StripedTableCell>
                          <StripedTableCell>Title</StripedTableCell>
                          <StripedTableCell>Owner</StripedTableCell>
                        </StripedTableRow>
                      </TableHead>

                      <TableBody>
                        <TableRowNoResults data={_meetings} />
                        {_meetings.map((meeting) => (
                          <StripedTableRow>
                            <StripedTableCell align="center">
                              {dayjs(meeting.startDate).format("MM/DD")}
                            </StripedTableCell>
                            <StripedTableCell align="center">
                              {getEventStartTime(meeting)}-
                              {getEventEndTime(meeting)}
                            </StripedTableCell>
                            <StripedTableCell>
                              {meeting.meetingTypeName}
                            </StripedTableCell>
                            <StripedTableCell>{meeting.title}</StripedTableCell>
                            <StripedTableCell>
                              {meeting.ownerLastName},{" "}
                              {meeting.ownerFirstName.substring(0, 1)}
                            </StripedTableCell>
                          </StripedTableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {_selectedTabId === "opsTab" && (
                  <TableContainer component={Paper} sx={{ mt: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <StripedTableRow>
                          <StripedTableCell align="center">
                            Date
                          </StripedTableCell>
                          <StripedTableCell align="center">
                            Time
                          </StripedTableCell>
                          <StripedTableCell>Type</StripedTableCell>
                          <StripedTableCell>Description</StripedTableCell>
                          <StripedTableCell>Status</StripedTableCell>
                          <StripedTableCell>Reject Reason</StripedTableCell>
                        </StripedTableRow>
                      </TableHead>
                      <TableBody>
                        <TableRowNoResults data={_serviceOps} />
                        {_serviceOps.map((op) => (
                          <StripedTableRow>
                            <StripedTableCell align="center">
                              {dayjs(op.eventDate).format("MM/DD")}
                            </StripedTableCell>
                            <StripedTableCell align="center">
                              {op.hourNum} h, {op.minuteNum} m
                            </StripedTableCell>
                            <StripedTableCell>{op.typeName}</StripedTableCell>
                            <StripedTableCell>
                              {op.description}
                            </StripedTableCell>
                            <StripedTableCell>
                              {getCommServiceStatusText(op.status)}
                            </StripedTableCell>
                            <StripedTableCell>
                              {isEmpty(op.rejectReason) && "--N/A--"}
                              {isEmpty(op.rejectReason) != null &&
                                op.rejectReason}
                            </StripedTableCell>
                          </StripedTableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </Container>
    </>
  );
};

export default StudentProfile;
